import React, { useState, useCallback, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from 'next-themes';
import {
  LayoutDashboard, Store, Truck, DollarSign, Menu, X, Pill, Building2,
  Crown, Settings, LogOut, Bell, ChevronRight, Globe, Sun, Moon, Monitor,
  Search, HelpCircle, User, ChevronDown
} from 'lucide-react';
import { getMessagesUnreadCount } from '@/lib/api';

const BRAND = '#1F4F46';
const BRAND_LIGHT = '#2A6A5E';
const BRAND_BG = 'rgba(31,79,70,0.08)';
const BRAND_BG_STRONG = 'rgba(31,79,70,0.15)';
const GOLD = '#C49B2A';
const SURFACE = '#FFFFFF';
const BG = '#F5F6F5';

const TABS = [
  { path: '/pharmacy-dashboard', icon: LayoutDashboard, labelKey: 'dashboard' },
  { path: '/apteka-control', icon: Store, labelKey: 'aptekas' },
  { path: '/deliveries', icon: Truck, labelKey: 'deliveries' },
  { path: '/payments', icon: DollarSign, labelKey: 'payments' },
];

const MORE_ITEMS = [
  { path: '/apteka-catalog', icon: Pill, labelKey: 'catalog' },
  { path: '/company-catalog', icon: Building2, labelKey: 'companyCatalog' },
  { path: '/notifications', icon: Bell, labelKey: 'notifications' },
  { path: '/connections', icon: Globe, labelKey: 'connections' },
  { path: '/subscription', icon: Crown, labelKey: 'subscription' },
  { path: '/settings', icon: Settings, labelKey: 'settings' },
  { path: '/help', icon: HelpCircle, labelKey: 'help' },
];

export default function MobileLayout() {
  const [moreOpen, setMoreOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { user, logout, workspaceName } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [msgUnread, setMsgUnread] = useState(0);
  const fetchUnread = useCallback(async () => {
    try { const { data } = await getMessagesUnreadCount(); setMsgUnread(data.count || 0); } catch {}
  }, []);
  useEffect(() => { fetchUnread(); const i = setInterval(fetchUnread, 30000); return () => clearInterval(i); }, [fetchUnread]);

  const handleLogout = async () => { setMoreOpen(false); await logout(); navigate('/home'); };

  const isTabActive = (path) => location.pathname === path;
  const isMoreActive = MORE_ITEMS.some(item => location.pathname === item.path);

  const moreLabels = {
    en: { catalog: 'My Catalog', companyCatalog: 'Company Catalog', notifications: 'Notifications', connections: 'Connections', subscription: 'Subscription', settings: 'Settings', help: 'Help / Report Issue', more: 'More', appearance: 'Appearance', light: 'Light', dark: 'Dark', auto: 'Auto', logout: 'Log Out', pharmacyOwner: 'Pharmacy Owner', pharmacyAdmin: 'Pharmacy Admin', pharmacy: 'Pharmacy', dashboard: 'Dashboard', aptekas: 'Aptekas', deliveries: 'Deliveries', payments: 'Payments', pharmDashboard: 'PHARMACY DASHBOARD' },
    ru: { catalog: 'Мой каталог', companyCatalog: 'Каталог компании', notifications: 'Уведомления', connections: 'Связи', subscription: 'Подписка', settings: 'Настройки', help: 'Помощь / Сообщить', more: 'Ещё', appearance: 'Оформление', light: 'Светлая', dark: 'Тёмная', auto: 'Авто', logout: 'Выйти', pharmacyOwner: 'Владелец аптеки', pharmacyAdmin: 'Админ аптеки', pharmacy: 'Аптека', dashboard: 'Главная', aptekas: 'Аптеки', deliveries: 'Доставки', payments: 'Платежи', pharmDashboard: 'ПАНЕЛЬ АПТЕКИ' },
    uk: { catalog: 'Мій каталог', companyCatalog: 'Каталог компанії', notifications: 'Сповіщення', connections: 'Зв\'язки', subscription: 'Підписка', settings: 'Налаштування', help: 'Допомога / Повідомити', more: 'Ще', appearance: 'Оформлення', light: 'Світла', dark: 'Темна', auto: 'Авто', logout: 'Вийти', pharmacyOwner: 'Власник аптеки', pharmacyAdmin: 'Адмін аптеки', pharmacy: 'Аптека', dashboard: 'Головна', aptekas: 'Аптеки', deliveries: 'Доставки', payments: 'Платежі', pharmDashboard: 'ПАНЕЛЬ АПТЕКИ' },
    uz: { catalog: 'Mening katalogim', companyCatalog: 'Kompaniya katalogi', notifications: 'Bildirishnomalar', connections: 'Ulanishlar', subscription: 'Obuna', settings: 'Sozlamalar', help: 'Yordam / Xabar', more: 'Yana', appearance: 'Ko\'rinish', light: 'Yorug\'', dark: 'Qorong\'i', auto: 'Avto', logout: 'Chiqish', pharmacyOwner: 'Dorixona egasi', pharmacyAdmin: 'Dorixona admini', pharmacy: 'Dorixona', dashboard: 'Bosh sahifa', aptekas: 'Dorixonalar', deliveries: 'Yetkazish', payments: 'To\'lovlar', pharmDashboard: 'DORIXONA PANELI' },
    tj: { catalog: 'Каталоги ман', companyCatalog: 'Каталоги ширкат', notifications: 'Огоҳиномаҳо', connections: 'Пайвастагиҳо', subscription: 'Обуна', settings: 'Танзимот', help: 'Кумак / Хабар', more: 'Бештар', appearance: 'Намуд', light: 'Равшан', dark: 'Торик', auto: 'Авто', logout: 'Баромадан', pharmacyOwner: 'Соҳиби доруханa', pharmacyAdmin: 'Админи доруханa', pharmacy: 'Доруханa', dashboard: 'Асосӣ', aptekas: 'Доруханаҳо', deliveries: 'Расонидан', payments: 'Пардохтҳо', pharmDashboard: 'ПАНЕЛИ ДОРУХАНA' },
  };
  const ml = moreLabels[language] || moreLabels.en;

  const roleName = {
    pharmacy_owner: ml.pharmacyOwner,
    pharmacy_admin: ml.pharmacyAdmin,
    apteka: ml.pharmacy,
  }[user?.role] || ml.pharmacy;

  const themes = [
    { key: 'light', icon: Sun, label: ml.light },
    { key: 'dark', icon: Moon, label: ml.dark },
    { key: 'system', icon: Monitor, label: ml.auto },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', minHeight: '100dvh', background: BG }}>
      {/* ─── Top Header ─── */}
      <header
        data-testid="mobile-header"
        style={{
          position: 'sticky', top: 0, zIndex: 40,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 16px', height: 56,
          background: BRAND,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 10, background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em',
          }}>Px</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
              {workspaceName || 'Pharm-X'}
            </div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', fontWeight: 500, letterSpacing: '0.02em' }}>
              {ml.pharmDashboard}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button
            onClick={() => navigate('/notifications')}
            data-testid="mobile-notifications-btn"
            style={{
              position: 'relative', background: 'rgba(255,255,255,0.1)', border: 'none',
              borderRadius: 10, padding: 8, cursor: 'pointer', color: '#fff',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Bell style={{ width: 18, height: 18 }} />
            {msgUnread > 0 && (
              <span style={{
                position: 'absolute', top: 2, right: 2, minWidth: 16, height: 16,
                borderRadius: 99, background: '#EF4444', color: '#fff',
                fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `2px solid ${BRAND}`,
              }}>{msgUnread > 9 ? '9+' : msgUnread}</span>
            )}
          </button>

          {/* Language Selector */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              data-testid="mobile-lang-btn"
              style={{
                background: 'rgba(255,255,255,0.1)', border: 'none',
                borderRadius: 10, padding: '6px 10px', cursor: 'pointer', color: '#fff',
                display: 'flex', alignItems: 'center', gap: 4,
                fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <Globe style={{ width: 14, height: 14 }} />
              {(language || 'en').toUpperCase().slice(0, 2)}
              <ChevronDown style={{ width: 12, height: 12, opacity: 0.6 }} />
            </button>
            {langOpen && (
              <>
                <div onClick={() => setLangOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 70 }} />
                <div
                  data-testid="mobile-lang-dropdown"
                  style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 71,
                    background: SURFACE, borderRadius: 12, overflow: 'hidden',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.15)', border: '1px solid rgba(0,0,0,0.08)',
                    minWidth: 150,
                  }}
                >
                  {[
                    { code: 'en', label: 'English', flag: 'EN' },
                    { code: 'ru', label: 'Русский', flag: 'RU' },
                    { code: 'uk', label: 'Українська', flag: 'UK' },
                    { code: 'uz', label: 'O\'zbekcha', flag: 'UZ' },
                  ].map((lng) => (
                    <button
                      key={lng.code}
                      onClick={() => { setLanguage(lng.code); setLangOpen(false); }}
                      data-testid={`mobile-lang-${lng.code}`}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                        padding: '11px 14px', border: 'none', background: 'none',
                        cursor: 'pointer', textAlign: 'left',
                        fontSize: 13, fontWeight: (language || 'en') === lng.code ? 700 : 500,
                        color: (language || 'en') === lng.code ? BRAND : '#374151',
                        borderBottom: '1px solid rgba(0,0,0,0.04)',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      <span style={{
                        width: 26, height: 20, borderRadius: 4,
                        background: (language || 'en') === lng.code ? BRAND_BG_STRONG : '#F3F4F6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 700, color: (language || 'en') === lng.code ? BRAND : '#6B7280',
                      }}>{lng.flag}</span>
                      {lng.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ─── Page Content ─── */}
      <main style={{ flex: 1, overflow: 'auto', paddingBottom: 80 }}>
        <Outlet />
      </main>

      {/* ─── Bottom Tab Bar ─── */}
      <nav
        data-testid="mobile-bottom-tabs"
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50,
          display: 'flex', alignItems: 'stretch',
          background: SURFACE,
          borderTop: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.06)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {TABS.map((tab) => {
          const active = isTabActive(tab.path);
          return (
            <NavLink
              key={tab.path}
              to={tab.path}
              data-testid={`mobile-tab-${tab.labelKey}`}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 3, textDecoration: 'none',
                padding: '10px 0 8px',
                color: active ? BRAND : '#9CA3AF',
                fontSize: 10, fontWeight: active ? 700 : 500,
                letterSpacing: '0.01em',
                position: 'relative',
                WebkitTapHighlightColor: 'transparent',
                transition: 'color 0.2s ease',
              }}
            >
              {active && <div style={{
                position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                width: 24, height: 3, borderRadius: 2, background: BRAND,
              }} />}
              <tab.icon style={{ width: 21, height: 21, strokeWidth: active ? 2.3 : 1.6 }} />
              <span>{ml[tab.labelKey]}</span>
            </NavLink>
          );
        })}

        {/* More Tab */}
        <button
          onClick={() => setMoreOpen(true)}
          data-testid="mobile-tab-more"
          style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 3, border: 'none', background: 'none',
            padding: '10px 0 8px',
            color: isMoreActive || moreOpen ? BRAND : '#9CA3AF',
            fontSize: 10, fontWeight: isMoreActive || moreOpen ? 700 : 500,
            letterSpacing: '0.01em', cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
            position: 'relative',
          }}
        >
          {(isMoreActive || moreOpen) && <div style={{
            position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
            width: 24, height: 3, borderRadius: 2, background: BRAND,
          }} />}
          <Menu style={{ width: 21, height: 21, strokeWidth: (isMoreActive || moreOpen) ? 2.3 : 1.6 }} />
          <span>{ml.more}</span>
        </button>
      </nav>

      {/* ─── More Menu Overlay ─── */}
      {moreOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setMoreOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 59,
              background: 'rgba(0,0,0,0.3)',
              animation: 'fadeIn 0.2s ease',
            }}
          />
          {/* Panel */}
          <div
            data-testid="mobile-more-overlay"
            style={{
              position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 60,
              background: SURFACE,
              borderTopLeftRadius: 20, borderTopRightRadius: 20,
              maxHeight: '85vh',
              display: 'flex', flexDirection: 'column',
              animation: 'slideUp 0.25s cubic-bezier(0.32, 0.72, 0, 1)',
              boxShadow: '0 -8px 40px rgba(0,0,0,0.12)',
            }}
          >
            {/* Handle */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D1D5DB' }} />
            </div>

            {/* User Card */}
            <div style={{
              margin: '4px 16px 12px', padding: '14px 16px',
              background: BRAND, borderRadius: 14,
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 12, background: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, fontWeight: 700, color: '#fff',
              }}>
                {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#fff', lineHeight: 1.3 }}>
                  {user?.name || user?.email || 'User'}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                  {roleName}
                </div>
              </div>
              <div style={{
                width: 8, height: 8, borderRadius: 4, background: '#34D399',
                boxShadow: '0 0 6px rgba(52,211,153,0.5)',
              }} />
            </div>

            {/* Menu Items */}
            <div style={{ flex: 1, overflow: 'auto', padding: '0 8px' }}>
              {MORE_ITEMS.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setMoreOpen(false); }}
                    data-testid={`mobile-more-${item.path.slice(1)}`}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 14,
                      padding: '13px 14px', border: 'none', borderRadius: 12,
                      background: active ? BRAND_BG_STRONG : 'transparent',
                      cursor: 'pointer', textAlign: 'left',
                      color: active ? BRAND : '#374151',
                      fontSize: 14, fontWeight: active ? 600 : 500,
                      marginBottom: 2,
                      WebkitTapHighlightColor: 'transparent',
                      transition: 'background 0.15s ease',
                    }}
                  >
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: active ? BRAND_BG_STRONG : '#F3F4F6',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <item.icon style={{ width: 17, height: 17, color: active ? BRAND : '#6B7280' }} />
                    </div>
                    <span style={{ flex: 1 }}>{ml[item.labelKey]}</span>
                    <ChevronRight style={{ width: 15, height: 15, color: '#D1D5DB' }} />
                  </button>
                );
              })}

              {/* Theme Row */}
              <div style={{
                margin: '8px 6px', padding: '14px',
                background: '#F9FAFB', borderRadius: 14,
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, color: '#9CA3AF', marginBottom: 10,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>
                  {ml.appearance}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {themes.map((th) => {
                    const active = theme === th.key;
                    return (
                      <button
                        key={th.key}
                        onClick={() => setTheme(th.key)}
                        data-testid={`mobile-theme-${th.key}`}
                        style={{
                          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          padding: '9px 0', borderRadius: 10,
                          border: active ? `2px solid ${BRAND}` : '2px solid transparent',
                          cursor: 'pointer', fontSize: 12, fontWeight: 600,
                          background: active ? BRAND_BG : '#fff',
                          color: active ? BRAND : '#6B7280',
                          transition: 'all 0.15s ease',
                          WebkitTapHighlightColor: 'transparent',
                        }}
                      >
                        <th.icon style={{ width: 14, height: 14 }} />
                        {th.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Logout */}
            <div style={{
              padding: '8px 16px 16px',
              paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            }}>
              <button
                onClick={handleLogout}
                data-testid="mobile-logout-btn"
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '13px', borderRadius: 12,
                  border: '1.5px solid rgba(239,68,68,0.2)',
                  background: 'rgba(239,68,68,0.05)', color: '#EF4444',
                  cursor: 'pointer', fontSize: 14, fontWeight: 600,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <LogOut style={{ width: 16, height: 16 }} />
                {ml.logout}
              </button>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
